import { Link } from "react-router-dom";

/** Attachment limits used on several pages. Keep in sync with the JSON-LD in entry-server.tsx. */
export const ATTACHMENT_LIMITS: { service: string; limit: string; note: string }[] = [
  { service: "Gmail", limit: "25 MB", note: "Larger files are converted to Google Drive links that require Google sign-in for some recipients." },
  { service: "Outlook / Microsoft 365", limit: "20 MB (up to 150 MB via OneDrive)", note: "Exchange server limits are often lower." },
  { service: "Apple Mail / iCloud", limit: "20 MB (Mail Drop up to 5 GB)", note: "Mail Drop links expire after 30 days." },
  { service: "WhatsApp", limit: "2 GB", note: "Media is recompressed unless sent as a document." },
  { service: "Telegram", limit: "2 GB (4 GB with Premium)", note: "Files are stored on Telegram's servers indefinitely." },
  { service: "Discord", limit: "10 MB (500 MB with Nitro)", note: "Attachments stay accessible via CDN links." },
  { service: "Slack", limit: "1 GB", note: "Requires workspace membership." },
  { service: "Microsoft Teams", limit: "250 GB via SharePoint", note: "Recipients need tenant access." },
  { service: "LimeShare", limit: "3 GB per file, 3 files (free, no account)", note: "Encrypted in the browser; auto-deleted; no recipient account." },
];

function LimitsTable() {
  return (
    <table>
      <thead>
        <tr>
          <th>Service</th>
          <th>Attachment limit</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {ATTACHMENT_LIMITS.map((r) => (
          <tr key={r.service}>
            <td>{r.service}</td>
            <td>{r.limit}</td>
            <td>{r.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Cta({ text = "Send files now — free, no account" }: { text?: string }) {
  return (
    <p style={{ marginTop: 22 }}>
      <Link to="/" className="btn" style={{ display: "inline-block", textDecoration: "none" }}>
        {text}
      </Link>
    </p>
  );
}

export function SendLargeFiles() {
  return (
    <section className="panel legal">
      <h2>How to send large files (2026 guide)</h2>
      <p className="hint">Every way to move a file that is too big for email — and what each one costs you in privacy, time and money.</p>

      <h3>Why the email bounced</h3>
      <p>
        Email was designed for text. Most providers cap attachments between 20 and 25 MB, and the message grows by about a third
        during encoding, so a 20 MB video already fails. Phone videos are 100 MB per minute in 4K; a RAW photo is 30–80 MB; a
        project archive is gigabytes.
      </p>
      <LimitsTable />

      <h3>Option 1 — a transfer link (fastest)</h3>
      <p>
        Upload once, send a link, the recipient downloads without an account. This is what LimeShare does, with one difference:
        the file is encrypted in your browser before upload, and the key stays in the link. The service cannot read, scan or
        leak the content, and the transfer deletes itself.
      </p>
      <ol>
        <li>Open limeshare.org and drop up to 3 files (3 GB each).</li>
        <li>Optionally set a password, a message, "burn after first download" and an expiry of 1–48 hours.</li>
        <li>Click Encrypt &amp; get link, then share the link or QR code.</li>
      </ol>

      <h3>Option 2 — cloud drives (Google Drive, Dropbox, OneDrive)</h3>
      <p>
        Good when the recipient already lives in the same ecosystem. Downsides: the provider stores and indexes the plaintext,
        recipients often need an account, sharing settings are easy to get wrong, and files linger forever unless you clean up.
      </p>

      <h3>Option 3 — messengers</h3>
      <p>
        WhatsApp and Telegram accept up to 2 GB, but recompress media, keep copies on their servers and tie the file to a chat.
        Fine for a clip to a friend; wrong for client deliverables or anything confidential.
      </p>

      <h3>Option 4 — compress or split</h3>
      <p>
        ZIP rarely helps with video or photos (already compressed). Splitting archives works but is painful for recipients.
        Use it only when no network transfer is possible.
      </p>

      <h3>Option 5 — physical media</h3>
      <p>Still the fastest for tens of terabytes. For everything under that, a link wins.</p>

      <h3>Choosing</h3>
      <ul>
        <li>Confidential (contracts, medical, unreleased work): end-to-end encrypted link with password.</li>
        <li>Casual (a video to family): any link service; prefer one that deletes automatically.</li>
        <li>Recurring with the same team: shared drive.</li>
      </ul>
      <Cta />
    </section>
  );
}

export function AttachmentLimits() {
  return (
    <section className="panel legal">
      <h2>Email and messenger attachment size limits (2026)</h2>
      <p className="hint">Reference table, updated as providers change their limits. Values are for standard consumer plans.</p>
      <LimitsTable />
      <h3>Why 25 MB is really about 18 MB</h3>
      <p>
        Attachments are Base64-encoded for transport, which adds roughly 33 %. A provider that allows 25 MB per message accepts
        about 18–19 MB of actual file. Add the message body, signatures and inline images and the practical ceiling is lower.
      </p>
      <h3>When the file is over the limit</h3>
      <p>
        Send a link instead of an attachment. With LimeShare the file is encrypted in your browser, the link carries the key, and
        the transfer deletes itself after 48 hours or the first download. No account for you or the recipient.
      </p>
      <Cta text="Send a file that is too large for email" />
    </section>
  );
}

export function WeTransferAlternative() {
  return (
    <section className="panel legal">
      <h2>WeTransfer alternative with end-to-end encryption</h2>
      <p className="hint">Honest comparison. WeTransfer is a polished product; the difference is who can see your files.</p>
      <table>
        <thead>
          <tr>
            <th>&nbsp;</th>
            <th>LimeShare</th>
            <th>WeTransfer (free)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Encryption</td>
            <td>End-to-end in your browser; the service never has the key</td>
            <td>In transit and at rest; the service can access files and previews</td>
          </tr>
          <tr>
            <td>Account</td>
            <td>None, ever</td>
            <td>Email verification for most transfers</td>
          </tr>
          <tr>
            <td>Free size</td>
            <td>3 files × 3 GB per transfer</td>
            <td>Around 3 GB per transfer, monthly caps apply</td>
          </tr>
          <tr>
            <td>Expiry</td>
            <td>1–48 h, burn on first download, destroy any time</td>
            <td>Typically 3 days</td>
          </tr>
          <tr>
            <td>File names visible to the service</td>
            <td>No (encrypted manifest)</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Ads and tracking</td>
            <td>None</td>
            <td>Advertising on download pages</td>
          </tr>
          <tr>
            <td>Password protection</td>
            <td>Free, Argon2id, never sent to the server</td>
            <td>Paid plans</td>
          </tr>
          <tr>
            <td>Open source client</td>
            <td>Yes (LSC1 protocol + client library)</td>
            <td>No</td>
          </tr>
        </tbody>
      </table>
      <p className="hint" style={{ marginTop: 10 }}>Competitor figures reflect public plan pages as of 2026 and may change.</p>
      <h3>Where WeTransfer is better</h3>
      <p>
        Long-standing brand, native desktop and mobile apps, team features, custom branding and integrations on paid plans. If you
        need those today and do not mind the provider holding your plaintext, it is a fine choice.
      </p>
      <h3>Where LimeShare is better</h3>
      <p>
        Anything you would not want a stranger at the provider — or anyone who breaches it — to open: client work, legal and
        medical documents, unreleased media, personal videos. Zero-knowledge means there is nothing to leak.
      </p>
      <Cta text="Try the encrypted alternative" />
    </section>
  );
}

export function EncryptedTransfer() {
  return (
    <section className="panel legal">
      <h2>Encrypted file transfer: what "encrypted" actually means</h2>
      <p className="hint">Three very different things hide behind the same word.</p>
      <h3>1. Encrypted in transit (TLS)</h3>
      <p>Protects the connection between you and the server. Every website has it. The server sees the plaintext.</p>
      <h3>2. Encrypted at rest</h3>
      <p>The disk is encrypted; the provider holds the key. Protects against stolen hardware, not against the provider, its staff, subpoenas or a breach of its systems.</p>
      <h3>3. End-to-end / zero-knowledge</h3>
      <p>
        The key is generated on your device and never shared with the service. Only the sender and the recipient can decrypt.
        The provider stores noise. This is how LimeShare works: AES-256-GCM in your browser, per-file keys derived with HKDF, an
        authenticated streaming format (LSC1) that detects truncation or tampering, and the key carried in the URL fragment that
        browsers never send to servers.
      </p>
      <h3>How to check a service's claim</h3>
      <ul>
        <li>Can you recover files if you lose the link or password? If yes, they have the key.</li>
        <li>Do they show previews or thumbnails on their site? Then they decrypt server-side.</li>
        <li>Is the client code published so the encryption can be inspected? It should be.</li>
      </ul>
      <p>
        Details of our implementation: <Link to="/security">security model</Link> · <Link to="/how-it-works">how it works</Link>.
      </p>
      <Cta text="Send an end-to-end encrypted file" />
    </section>
  );
}

export function SendAnonymously() {
  return (
    <section className="panel legal">
      <h2>Send files anonymously — without an account, email or app</h2>
      <p className="hint">What anonymity means here, what it does not, and how to get the most of it.</p>
      <h3>What LimeShare does not ask for</h3>
      <ul>
        <li>No account, email, phone number or name — for the sender or the recipient.</li>
        <li>No cookies for anonymous use, no advertising or analytics scripts.</li>
        <li>No raw IP addresses stored: only a salted hash rotated daily, kept for three days for abuse limits.</li>
        <li>No file names or contents: everything is encrypted before upload.</li>
      </ul>
      <h3>What remains visible</h3>
      <ul>
        <li>Your network operator sees that you connected to limeshare.org and roughly how much data moved.</li>
        <li>Whoever you send the link to can open it — the link is the key. Add a password sent through a different channel.</li>
        <li>Metadata inside the file itself (EXIF in photos, author fields in documents) is untouched. Strip it before sending if it matters.</li>
      </ul>
      <h3>Practical tips</h3>
      <ol>
        <li>Use "burn after first download" for one-recipient handoffs.</li>
        <li>Set a short expiry (1 hour) when the recipient is online now.</li>
        <li>Use a VPN or Tor if hiding your network location from us matters; the service works over both.</li>
        <li>Destroy the transfer from "Sent from this browser" once the recipient confirms.</li>
      </ol>
      <Cta text="Send anonymously" />
    </section>
  );
}
